import { useRef, useState } from 'react';
import type {
  ChangeEvent,
  FocusEvent,
  MouseEvent,
  SyntheticEvent
} from 'react';
import { NUMERIC_INPUT_ATTRS } from '../../../utils';
import { clampCaretIndex, findBoxIndexAtX } from './verificationCodeSelection';

const CODE_LENGTH = 6;
// Fixed, stable keys for the six boxes - they never get added, removed, or reordered, but a
// key derived from the render-loop index still trips the "no array index as key" lint rule.
const BOX_KEYS = [
  'box-0',
  'box-1',
  'box-2',
  'box-3',
  'box-4',
  'box-5'
] as const;

function getBorderClass(invalid: boolean | undefined, isActive: boolean) {
  if (invalid) {
    return 'border-2 border-error';
  }
  if (isActive) {
    return 'border-2 border-blue-50';
  }
  return 'border border-gray-60';
}

interface VerificationCodeInputProps {
  id?: string;
  name?: string;
  value: string;
  invalid?: boolean;
  onChange: (value: string) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
}

// A single real input, made invisible and stretched over the boxes, is what makes SMS
// autofill and pasting a full code work reliably across mobile browsers - splitting the
// code across six real inputs breaks both, since the OS/browser only knows how to target
// a single field with the suggested code.
function VerificationCodeInput({
  id,
  name,
  value,
  invalid,
  onChange,
  onBlur
}: Readonly<VerificationCodeInputProps>) {
  const [isFocused, setIsFocused] = useState(false);
  // Box index for the blue "active" highlight - tracks the real caret position, not just
  // value.length, since a click or arrow key can move it into the middle of the code.
  const [caretIndex, setCaretIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const boxRefs = useRef<Array<HTMLDivElement | null>>([]);

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const typed = event.target.value.replace(/\D/g, '');
    // A digit typed while the caret sits on an existing digit (not appended at the end)
    // should replace that digit - the browser's own collapsed-caret insert would instead push
    // every digit after it one place to the right.
    const isMidEditKeystroke =
      caretIndex < value.length && typed.length === value.length + 1;
    const nextValue = isMidEditKeystroke
      ? (
          value.slice(0, caretIndex) +
          typed[caretIndex] +
          value.slice(caretIndex + 1)
        ).slice(0, CODE_LENGTH)
      : typed.slice(0, CODE_LENGTH);

    // Force the DOM in sync ourselves - a mid-edit replace needs the DOM to reflect our
    // spliced result rather than the browser's raw inserted one, and fast typing past
    // CODE_LENGTH can otherwise outrun React's re-render.
    if (event.target.value !== nextValue) {
      event.target.value = nextValue;
    }
    onChange(nextValue);

    const nextCaretIndex = isMidEditKeystroke
      ? Math.min(caretIndex + 1, nextValue.length)
      : (event.target.selectionStart ?? nextValue.length);
    event.target.setSelectionRange(nextCaretIndex, nextCaretIndex);
    setCaretIndex(nextCaretIndex);
  }

  function handleFocus(event: FocusEvent<HTMLInputElement>) {
    setIsFocused(true);
    // Reset to the end rather than trust the browser's inconsistent tab-focus default - ready
    // to append, matching normal use. A box click overrides this right after in handleMouseDown.
    event.target.setSelectionRange(value.length, value.length);
    setCaretIndex(value.length);
  }

  function handleBlur(event: FocusEvent<HTMLInputElement>) {
    setIsFocused(false);
    onBlur?.(event);
  }

  // Just tracks where the browser's own collapsed caret ends up after arrow keys, Home/End,
  // or a click that missed every box - never repositions it. An earlier version forced a real
  // one-character selection here so typing would overwrite it, but browsers collapse an
  // active selection to its *start* on ArrowLeft - re-selecting after every move made the
  // caret stick in place instead of moving left. handleChange now handles replace-in-place
  // itself, so this only needs to observe.
  function handleCaretSync(event: SyntheticEvent<HTMLInputElement>) {
    setCaretIndex(event.currentTarget.selectionStart ?? value.length);
  }

  // The input paints on top for hit-testing (absolutely-positioned beats static siblings,
  // which is also why clicking anywhere already focuses it). preventDefault() skips the
  // browser's own caret placement, which would use the invisible input's glyph metrics rather
  // than the spaced-out boxes the fan actually clicked.
  function handleMouseDown(event: MouseEvent<HTMLInputElement>) {
    const boxRects = boxRefs.current.map(
      (box) => box?.getBoundingClientRect() ?? null
    );
    const clickedIndex = findBoxIndexAtX(event.clientX, boxRects);
    if (clickedIndex === -1) return;

    event.preventDefault();
    const input = inputRef.current;
    if (!input) return;
    input.focus();
    const position = clampCaretIndex(clickedIndex, value.length);
    input.setSelectionRange(position, position);
    setCaretIndex(position);
  }

  return (
    // This project's Tailwind theme overrides --spacing to 8px (not the standard 4px), so
    // every dimension here is a literal pixel value - the numbered scale (w-10, gap-2, etc.)
    // would render roughly 2x too large.
    <div className="relative flex justify-center gap-[7px] sm:gap-[6px]">
      <input
        ref={inputRef}
        id={id}
        name={name}
        type="text"
        {...NUMERIC_INPUT_ATTRS}
        autoComplete="one-time-code"
        // No maxLength here - the browser truncates pasted text to it natively, before
        // handleChange ever runs, which would cut a code short (or lose it entirely) if the
        // pasted string has separators or surrounding text. Capping happens below instead,
        // after stripping non-digits.
        value={value}
        onChange={handleChange}
        onMouseDown={handleMouseDown}
        onMouseUp={handleCaretSync}
        onKeyUp={handleCaretSync}
        onFocus={handleFocus}
        onBlur={handleBlur}
        aria-label="Verification code"
        aria-invalid={invalid}
        data-testid="tax_verification_code_input"
        className="absolute inset-0 h-full w-full cursor-text opacity-0"
      />
      {BOX_KEYS.map((key, index) => {
        const isActive = isFocused && index === caretIndex;
        const borderClass = getBorderClass(invalid, isActive);

        return (
          <div
            key={key}
            ref={(box) => {
              boxRefs.current[index] = box;
            }}
            aria-hidden="true"
            data-testid={`verification_code_box_${index}`}
            className={`flex h-[51px] w-5 items-center justify-center rounded-[4px] text-lg font-medium text-gray-90 sm:w-[47px] ${borderClass}`}
          >
            {value[index] ?? '–'}
          </div>
        );
      })}
    </div>
  );
}

export { VerificationCodeInput };
